import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import type { PanelWithData } from '@2bforest/common';

interface StatPanelPreviewCardProps {
  panel: PanelWithData;
}

const PALETTE = ['#F39200', '#1A5F3F', '#4E79A7', '#F28E2B', '#E15759', '#76B7B2'];

function NoData() {
  return (
    <div className="text-center text-muted py-4">
      <em>No data</em>
    </div>
  );
}

export default function StatPanelPreviewCard({ panel }: StatPanelPreviewCardProps) {
  function renderContent() {
    switch (panel.displayType) {
      case 'infographic': {
        if (panel.computedValue === undefined || panel.computedValue === null) {
          return <NoData />;
        }
        return (
          <div className="text-center py-3">
            {panel.iconUrl && (
              <img
                src={panel.iconUrl}
                alt="Panel icon"
                style={{
                  width: 64,
                  height: 64,
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto 0.5rem',
                }}
              />
            )}
            <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#F39200' }}>
              {panel.computedValue}
            </div>
          </div>
        );
      }

      case 'bar_chart': {
        const chart = panel.computedChart;
        if (!chart || chart.labels.length === 0) return <NoData />;
        const data = chart.labels.map((label, i) => ({
          name: label,
          value: chart.datasets[0]?.values[i] ?? 0,
        }));
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Bar dataKey="value" fill="#F39200">
                <LabelList dataKey="value" position="top" style={{ fontSize: 9 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'stacked_bar_chart': {
        const chart = panel.computedChart;
        if (!chart || chart.labels.length === 0) return <NoData />;
        const data = chart.labels.map((label, i) => {
          const entry: Record<string, string | number> = { name: label };
          for (const ds of chart.datasets) {
            entry[ds.label ?? 'value'] = ds.values[i] ?? 0;
          }
          return entry;
        });
        return (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data} margin={{ top: 16, right: 8, left: 0, bottom: 4 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {chart.datasets.map((ds, idx) => (
                <Bar
                  key={ds.label ?? idx}
                  dataKey={ds.label ?? 'value'}
                  stackId="stack"
                  fill={PALETTE[idx % PALETTE.length]}
                >
                  <LabelList
                    dataKey={ds.label ?? 'value'}
                    position="inside"
                    style={{ fontSize: 9, fill: '#fff' }}
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      }

      case 'pie_chart': {
        const chart = panel.computedChart;
        if (!chart || chart.labels.length === 0) return <NoData />;
        const data = chart.labels.map((label, i) => ({
          name: label,
          value: chart.datasets[0]?.values[i] ?? 0,
        }));
        return (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                {data.map((_, idx) => (
                  <Cell key={idx} fill={PALETTE[idx % PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        );
      }

      case 'table': {
        const table = panel.computedTable;
        if (!table || table.rows.length === 0) return <NoData />;
        return (
          <div style={{ overflowX: 'auto', maxHeight: 220, overflowY: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  {table.headers.map((h, i) => (
                    <th key={i} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                        {cell ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      default:
        return <NoData />;
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title">{panel.title}</h4>
        {panel.subtitle && <div className="card-subtitle text-muted">{panel.subtitle}</div>}
      </div>
      <div className="card-body">{renderContent()}</div>
      {(panel.computedFooter1 || panel.computedFooter2) && (
        <div className="card-footer text-muted" style={{ fontSize: '0.75rem' }}>
          {panel.computedFooter1 && <div>{panel.computedFooter1}</div>}
          {panel.computedFooter2 && <div>{panel.computedFooter2}</div>}
        </div>
      )}
    </div>
  );
}
